import React, { useState, useEffect } from "react";
import styles from "../../assets/styles/Homepage/FaqSection.module.css";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

  
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/faq`;

const imageAnim = {
  hidden: { opacity: 0, scale: 0.85, x: -60 },
  visible: {
    opacity: 1,
    scale: 1,
    x: 0,
    transition: { duration: 0.9, ease: "easeOut" },
  },
};

const contentAnim = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const FaqSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [faqSections, setFaqSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API);
        setFaqSections(res.data.data || []);
      } catch (err) {
        setError("Failed to load FAQs. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  const toggleFaq = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // Use the first section from API, or fall back to empty
  const section = faqSections[0] || null;
  const faqs = section?.faqs || [];
  const heading = section?.heading || "Frequently Asked Questions";
  const subheading =
    section?.subheading ||
    "Our experienced instructors are here to inspire, motivate, and help you grow one breath at a time.";
  const imageUrl = section?.image
    ? `${ENV_API_BASE_URL}/uploads/${section.image}`
    : null;

  if (loading) {
    return (
      <section className={styles.faqSection}>
        <div className="container">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                border: "3px solid #e2e6f0",
                borderTopColor: "#4f6ef7",
                borderRadius: "50%",
                animation: "spin 0.7s linear infinite",
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.faqSection}>
        <div className="container">
          <p style={{ textAlign: "center", color: "#e53935", padding: "40px 0" }}>
            {error}
          </p>
        </div>
      </section>
    );
  }

  if (!section || faqs.length === 0) {
    return null; // Nothing to show if no data
  }

  return (
    <section className={styles.faqSection}>
      <div className="container">
        <div className="row align-items-center">

          {/* IMAGE */}
          {imageUrl && (
            <motion.div
              className="col-lg-5 mb-4 mb-lg-0"
              variants={imageAnim}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className={styles.imageWrapper}>
                <img src={imageUrl} alt={heading} />
              </div>
            </motion.div>
          )}

          {/* FAQ CONTENT */}
          <motion.div
            className={imageUrl ? "col-lg-7" : "col-lg-12"}
            variants={contentAnim}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className={styles.faqContent}>
              <h2>{heading}</h2>
              {subheading && (
                <p className={styles.subText}>{subheading}</p>
              )}

              <div className={styles.faqList}>
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`${styles.faqItem} ${
                      activeIndex === index ? styles.active : ""
                    }`}
                    onClick={() => toggleFaq(index)}
                  >
                    {/* QUESTION */}
                    <div className={styles.question}>
                      <span>{faq.question}</span>

                      <motion.span
                        className={styles.icon}
                        animate={{ rotate: activeIndex === index ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        +
                      </motion.span>
                    </div>

                    {/* ANSWER */}
                    <AnimatePresence>
                      {activeIndex === index && (
                        <motion.div
                          className={styles.answer}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: "easeOut" }}
                        >
                          <p>{faq.answer}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default FaqSection;